<script setup lang="ts">
import { ref, onMounted, reactive, computed } from 'vue';
import type { AuthUser } from '../../core/state/auth-store';
import {
  fetchHero, createHero, fetchStats, CLASS_LABELS,
  type HeroProfile, type HeroClass, type EffectiveStats,
} from '../game/hero-api';
import {
  fetchInventory, SLOTS,
  type Inventory as GearInventory, type Artifact, type EquipSlot,
} from '../game/inventory-api';
import wordmark from '../assets/omerta/wordmark.png';
import cashIcon from '../assets/omerta/icons/cash.webp';
import goldBarsIcon from '../assets/omerta/icons/gold-bars.webp';
import nerveIcon from '../assets/omerta/icons/nerve.webp';
import CityMap from './CityMap.vue';
import Districts from './Districts.vue';
import Tutorial from './Tutorial.vue';
import GoldReserve from './GoldReserve.vue';
import CountingHouse from './CountingHouse.vue';
import ToastHost from './ToastHost.vue';
import ConfirmHost from './ConfirmHost.vue';
import { toast } from '../game/toast';
import { confirmAction } from '../game/confirm';
import { consumePendingReferral } from '../game/referral-api';
import { useRegenClock, fmtEta } from '../game/regen-clock';
import { classArt, BG } from '../game/art';

// Dim noir ambience behind the whole app so panels never float over flat black.
const shellStyle =
  `background: linear-gradient(rgba(8,5,16,0.93), rgba(8,5,16,0.965)), url(${BG.office}) center / cover fixed no-repeat, var(--color-bg)`;
import Inventory from './Inventory.vue';
import ItemModal from './ItemModal.vue';
import HeroCard from './HeroCard.vue';
import HeroDossier from './HeroDossier.vue';
import Market from './Market.vue';
import Jobs from './Jobs.vue';
import Chat from './Chat.vue';
import Rankings from './Rankings.vue';
import Dungeons from './Dungeons.vue';
import Arena from './Arena.vue';
import Settings from './Settings.vue';
import type { Nerve } from '../game/map-api';

const props = defineProps<{ user: AuthUser; onLogout: () => void }>();

/**
 * Signing out is a one-click action sitting in the top bar next to the
 * currency chips, so a mis-tap used to drop the player straight back to the
 * login screen. Route it through the same calling-card confirm rail every
 * other irreversible action uses.
 */
async function onSignOut() {
  if (await confirmAction({
    kicker: 'The Family',
    title: 'Sign out?',
    note: 'Cash, nerve and every timer keep running while you are away.',
    confirmLabel: 'Sign out',
    cancelLabel: 'Stay',
    danger: true,
  })) props.onLogout();
}

const loading = ref(true);
const submitting = ref(false);
const error = ref('');
const hero = ref<HeroProfile | null>(null);
const stats = ref<EffectiveStats | null>(null);
const inventory = ref<GearInventory | null>(null);
const showTutorial = ref(true);
const view = ref<'profile' | 'map' | 'districts' | 'market' | 'jobs' | 'chat' | 'rankings' | 'dungeons' | 'pit' | 'reserve' | 'counting' | 'settings'>('profile');
// District the City Map should smoothly pan to on open ("Take it on the map").
// Cleared once the map consumes it, and on any plain nav to the map.
const mapFocus = ref<string | null>(null);
function onNav(tab: typeof view.value) {
  if (tab === 'map') mapFocus.value = null; // plain nav: no forced pan
  view.value = tab;
}

/** The tutorial overlay drives the backdrop behind itself so each beat
 *  previews the screen it's actually describing (Pit beat -> Pit view, ...). */
function onTutorialNav(v: string) {
  view.value = v as typeof view.value;
}

// Primary navigation — shared by the desktop rail and the mobile burger drawer.
const NAV_ITEMS = [
  { key: 'profile', label: 'Hero' },
  { key: 'map', label: 'City Map' },
  { key: 'districts', label: 'Districts' },
  { key: 'market', label: 'The Quarter' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'chat', label: 'Chat' },
  { key: 'rankings', label: 'Rankings' },
  { key: 'dungeons', label: 'Heists' },
  { key: 'pit', label: 'The Pit' },
] as const;

// Mobile burger drawer.
const menuOpen = ref(false);
function goTo(tab: typeof view.value) {
  onNav(tab);
  menuOpen.value = false;
}
function onGotoMap(districtId?: string) {
  mapFocus.value = districtId ?? null;
  view.value = 'map';
}
/** Which Quarter tab to land on — set right before switching view so cash/nerve
 *  chips can deep-link into the merged commerce screen (e.g. nerve → Supplies). */
const marketInitialTab = ref<'rack' | 'street' | 'supplies' | 'sell'>('rack');
function openMarket(initialTab: typeof marketInitialTab.value = 'rack') {
  marketInitialTab.value = initialTab;
  view.value = 'market';
}

/** GUIDE SPOTLIGHT — a header currency chip routes to the store that refills
 *  that resource and briefly pulses the matching cards (`.guide-glow`), so the
 *  player lands with their eye already on the right thing. */
const guide = ref<'cash' | 'gold' | 'nerve' | null>(null);
let guideTimer = 0;
function chipTo(target: 'cash' | 'gold' | 'nerve') {
  guide.value = target;
  window.clearTimeout(guideTimer);
  guideTimer = window.setTimeout(() => { guide.value = null; }, 4200);
  if (target === 'cash') view.value = 'counting';
  else if (target === 'gold') view.value = 'reserve';
  else openMarket('supplies');
}

function onNerve(n: Nerve) {
  if (!hero.value) return;
  hero.value.hero.nerve = { current: n.current, max: n.max };
  // Map moves re-anchor the regen countdown — without this the chip's timer
  // keeps counting from the pre-spend fraction and shows a wrong ETA.
  if (n.regen) hero.value.hero.regen = { ...hero.value.hero.regen, nerve: n.regen };
}

/** Header countdowns: next nerve point / HP back to full (client-ticked). */
const nerveClock = useRegenClock(() =>
  hero.value ? { anchor: hero.value.hero.regen?.nerve, max: hero.value.hero.nerve.max } : null);

/** Item detail modal state — shared by the Stash grid, the hero-card equipped
 *  strip and the dossier paperdoll, so exactly one ItemModal exists at a time. */
const inspect = ref<{ artifact: Artifact; slot: EquipSlot } | null>(null);
/** Filter the Stash grid to a slot — set when the player clicks an empty
 *  equipped-strip slot on the hero card ("show me what I could put here"). */
const stashFilter = ref<EquipSlot | 'ALL'>('ALL');

function openInspect(artifact: Artifact, slot: EquipSlot) {
  inspect.value = { artifact, slot };
}
function onFilterRequest(slot: EquipSlot) {
  stashFilter.value = slot;
}
async function onGearUpdated(inv: GearInventory) {
  inventory.value = inv;
  inspect.value = null;
  await refresh(); // gear mutations can change cash (repair/upgrade/sell) — refresh header + stats
}

async function refreshInventory() {
  if (!hero.value) return;
  try { inventory.value = await fetchInventory(); } catch { /* non-fatal */ }
}

async function refresh() {
  try { hero.value = await fetchHero(); } catch { /* keep current */ }
  if (hero.value) { try { stats.value = await fetchStats(); } catch { /* non-fatal */ } }
  // The bag too: Market buys, fight loot and sales all land here via @updated,
  // and each of them changes the stash — without this the bought item only
  // showed up after a hard page refresh.
  await refreshInventory();
}

const form = reactive<{ name: string; cls: HeroClass | null }>({ name: '', cls: null });

/**
 * The viewport lock (h-full + .profile-main{overflow:hidden} >=1101px) exists so
 * the PROFILE's three columns can scroll inside their own panels. `view` defaults
 * to 'profile', so before a hero exists the CREATE screen was inheriting that
 * lock: full-bleed max-w-[1600px] AND a page that cannot scroll, which clipped
 * everything past the fold — the Create hero button included — with no way to
 * reach it. Creation is an ordinary scrolling column; it only applies once a
 * hero actually exists.
 */
const isProfileView = computed(() => view.value === 'profile' && !!hero.value);

/**
 * The four classes ARE the four landing wanted-posters (the in-game portraits
 * were regenerated from those mugs), so the case numbers, stamps and rap-sheet
 * dots are carried over 1:1 from landing/index.html — same men, same file.
 * The rap sheet also earns its keep: it says what a trade DOES at a glance,
 * which the one-line blurb alone never did.
 */
interface ClassPoster {
  key: HeroClass;
  blurb: string;
  case: string;
  stamp: string;
  rot: string;
  stats: { k: string; v: number }[];
}

const CLASSES: ClassPoster[] = [
  { key: 'WARRIOR', blurb: 'Bruiser. Soaks hits, hits back harder.',
    case: '4811', stamp: 'At Large', rot: '-1.7deg',
    stats: [{ k: 'Muscle', v: 5 }, { k: 'Brains', v: 2 }, { k: 'Heat', v: 4 }] },
  { key: 'PALADIN', blurb: 'The boss. Balanced, keeps the crew standing.',
    case: '4829', stamp: 'Most Wanted', rot: '1.5deg',
    stats: [{ k: 'Muscle', v: 3 }, { k: 'Brains', v: 5 }, { k: 'Heat', v: 2 }] },
  { key: 'ARCHER', blurb: 'Fast trigger. High damage, thin skin.',
    case: '4792', stamp: 'At Large', rot: '-1.1deg',
    stats: [{ k: 'Muscle', v: 4 }, { k: 'Brains', v: 3 }, { k: 'Heat', v: 5 }] },
  { key: 'MAGE', blurb: 'The fixer. Burst and dirty tricks.',
    case: '4760', stamp: 'At Large', rot: '1.9deg',
    stats: [{ k: 'Muscle', v: 1 }, { k: 'Brains', v: 4 }, { k: 'Heat', v: 1 }] },
];

/** Rap-sheet pips. The label carries the real value for AT (see aria-label). */
const dots = (v: number): string => '●'.repeat(v) + '○'.repeat(5 - v);

onMounted(async () => {
  try {
    hero.value = await fetchHero();
    if (hero.value) {
      try { stats.value = await fetchStats(); } catch { /* non-fatal */ }
      await refreshInventory();
    }
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    loading.value = false;
  }

  // Referral bonus — claims a code captured pre-auth (see main.ts). Silent no-op
  // when there's nothing pending, already attempted, or the claim fails.
  const reward = await consumePendingReferral();
  if (reward != null) {
    toast(`Referral bonus: +${reward} cash`, { icon: cashIcon });
    if (hero.value) await refresh(); // pick up the credited cash on the header chip
  }
});

async function submit() {
  error.value = '';
  if (form.name.trim().length < 2) { error.value = 'Pick a name (2+ characters).'; return; }
  if (!form.cls) { error.value = 'Choose a class.'; return; }
  submitting.value = true;
  try {
    hero.value = await createHero(form.name.trim(), form.cls);
    toast(`Welcome to the family, ${hero.value.hero.name}`, { icon: classArt(form.cls) });
    // Full refresh, not just the bag: `stats` must stop being null right away
    // or the hero card renders base stats until the first unrelated refetch.
    await refresh();
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="h-screen flex flex-col text-text overflow-hidden" :style="shellStyle">
    <!-- top bar — desktop (lg+, 1024px) shows the full inline rail; below lg the
         tabs collapse into a burger drawer, leaving a clean logo · chips · menu row -->
    <header class="shrink-0 flex flex-wrap items-center gap-x-2 sm:gap-x-4 gap-y-2 px-3 sm:px-6 py-2 sm:py-3 border-b border-border">
      <img :src="wordmark" alt="Mafinini" class="h-7 lg:h-9 object-contain shrink-0" />

      <!-- DESKTOP inline nav — flex-wrap so a tight 1024–1280px rail folds to a
           second row instead of forcing horizontal scroll (zero side-scroll rule) -->
      <nav class="hidden lg:flex flex-wrap items-center gap-x-1 gap-y-1.5 lg:ml-4">
        <template v-if="hero">
          <button
            v-for="item in NAV_ITEMS" :key="item.key"
            @click="onNav(item.key)"
            :class="['tab-deco shrink-0', view === item.key && 'tab-deco-active']"
          >{{ item.label }}</button>
        </template>
        <button
          :class="['tab-deco shrink-0', view === 'settings' && 'tab-deco-active']"
          title="Account, papers & purchases"
          @click="view = 'settings'"
        >Settings</button>
      </nav>

      <!-- MOBILE burger — pushed to the right edge of row 1 -->
      <button
        class="hdr-burger lg:hidden ml-auto"
        @click="menuOpen = true" aria-label="Open menu"
      >
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      <!-- CURRENCY CHIPS — own full row below lg, inline (pushed right) on desktop -->
      <div v-if="hero" class="w-full flex items-center gap-2 lg:contents">
        <button class="chip-deco lg:ml-auto" title="Get cash — launder bars at the Counting House" @click="chipTo('cash')">
          <img :src="cashIcon" alt="cash" class="h-6 w-6 object-contain shrink-0" />
          <span class="font-heading text-gold">{{ hero.progression.cash.toLocaleString() }}</span>
        </button>
        <button class="chip-deco" title="Buy gold bars at the Gold Reserve" @click="chipTo('gold')">
          <img :src="goldBarsIcon" alt="gold bars" class="h-6 w-6 object-contain shrink-0" />
          <span class="font-heading text-gold-bright">{{ hero.progression.goldBars.toLocaleString() }}</span>
        </button>
        <button class="chip-deco" :title="nerveClock && !nerveClock.full ? `Next nerve in ${fmtEta(nerveClock.nextIn)} — or refill now at The Quarter` : 'Refill nerve — Stiff Drink at The Quarter'" @click="chipTo('nerve')">
          <img :src="nerveIcon" alt="nerve" class="h-6 w-6 object-contain shrink-0" />
          <span class="font-heading text-text whitespace-nowrap">{{ hero.hero.nerve.current }}/{{ hero.hero.nerve.max }}<span v-if="nerveClock && !nerveClock.full" class="font-body text-[11px] tabular-nums text-text-dim"> · {{ fmtEta(nerveClock.nextIn) }}</span></span>
        </button>
      </div>

      <!-- DESKTOP sign out -->
      <button
        class="hidden lg:inline-flex btn-deco btn-deco--blood btn-deco--sm shrink-0"
        @click="onSignOut"
      >Sign out</button>
    </header>

    <!-- ── MOBILE MENU DRAWER (burger) ─────────────────────────────────── -->
    <Transition name="drawer">
      <div v-if="menuOpen" class="hdr-drawer-scrim xl:hidden" @click.self="menuOpen = false">
        <aside class="hdr-drawer" role="dialog" aria-label="Menu">
          <div class="hdr-drawer-head">
            <img :src="wordmark" alt="Mafinini" class="h-7 object-contain" />
            <button class="hdr-drawer-x" @click="menuOpen = false" aria-label="Close menu">
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
          </div>
          <nav class="hdr-drawer-nav">
            <template v-if="hero">
              <button
                v-for="item in NAV_ITEMS" :key="item.key"
                :class="['hdr-drawer-item', view === item.key && 'is-active']"
                @click="goTo(item.key)"
              >{{ item.label }}</button>
            </template>
            <button
              :class="['hdr-drawer-item', view === 'settings' && 'is-active']"
              @click="view = 'settings'; menuOpen = false"
            >Settings</button>
          </nav>
          <button class="btn-deco btn-deco--blood btn-deco--sm hdr-drawer-out" @click="menuOpen = false; onSignOut()">Sign out</button>
        </aside>
      </div>
    </Transition>

    <!-- CITY MAP — full-screen, fills the viewport below the top bar -->
    <div v-if="!loading && hero && view === 'map'" class="flex-1 min-h-0">
      <CityMap
        :hero-class="hero?.hero.class" :hero-level="hero?.progression.level" :hero-id="hero?.hero.id"
        :hero-hp="hero?.hero.stats.hp" :hero-max-hp="stats?.effective.maxHp ?? hero?.hero.stats.maxHp"
        :hero-full-hp="stats?.base.maxHp" :hero-hp-regen="hero?.hero.regen?.hp"
        :cash="hero?.progression.cash" :gold-bars="hero?.progression.goldBars" :focus-district="mapFocus"
        @nerve="onNerve" @updated="refresh" @focused="mapFocus = null"
      />
    </div>

    <!-- every other view — scrollable centered column; the PROFILE view instead
         locks to the viewport (page never scrolls — columns manage their own
         height, the stash scrolls inside its panel) down to the 1100px
         single-column breakpoint, where it falls back to page scroll. -->
    <main v-else class="flex-1 min-h-0" :class="isProfileView ? 'profile-main' : 'overflow-y-auto'">
      <div :class="isProfileView ? 'h-full max-w-[1600px] mx-auto px-6 py-4' : 'max-w-[1100px] mx-auto px-6 py-10'">
      <p v-if="loading" class="text-text-dim">Loading…</p>

      <!-- error -->
      <p v-if="error" class="mb-4 text-sm text-error">{{ error }}</p>

      <!-- CREATE (settings stays reachable even before the hero exists) -->
      <section v-if="!loading && !hero && view !== 'settings'" class="make">
        <header class="make-head">
          <p class="make-kicker">By order of The Commission</p>
          <h1 class="make-title">Make your bones</h1>
          <span class="make-rule"></span>
          <p class="make-sub">Every made man starts somewhere. Pick a name and a trade.</p>
        </header>

        <!-- the docket: a typed case-file line, not a full-bleed bar -->
        <div class="docket">
          <label class="docket-label" for="hero-name">Name</label>
          <input
            id="hero-name"
            v-model="form.name"
            maxlength="50"
            placeholder="e.g. Vito Corleone"
            class="docket-input"
          />
        </div>

        <!-- the wall: pick your man off the board. A radio group, not buttons —
             "one of four" is exactly what a radiogroup is for, and it hands us
             arrow-key nav + the right AT semantics for free. -->
        <fieldset class="board">
          <legend class="sr-only">Pick a trade</legend>
          <label
            v-for="c in CLASSES" :key="c.key"
            class="poster"
            :class="{ 'poster--on': form.cls === c.key }"
            :style="{ '--rot': c.rot }"
          >
            <input
              class="sr-only poster-radio"
              type="radio"
              name="hero-class"
              :value="c.key"
              v-model="form.cls"
            />
            <span class="pin" aria-hidden="true"></span>
            <span class="paper">
              <span class="pstamp">{{ c.stamp }}</span>
              <span class="made" aria-hidden="true">Made</span>
              <span class="wanted">Wanted</span>
              <span class="doa">&#9670;&nbsp; Dead or Alive &nbsp;&#9670;</span>
              <span class="mug">
                <img :src="classArt(c.key)" :alt="CLASS_LABELS[c.key]" />
                <span class="mugcap">Case No. {{ c.case }}</span>
              </span>
              <span class="pname">{{ CLASS_LABELS[c.key] }}</span>
              <span class="crimes">{{ c.blurb }}</span>
              <span class="rap">
                <span class="rsrow" v-for="s in c.stats" :key="s.k">
                  <span>{{ s.k }}</span>
                  <span class="dots" :aria-label="`${s.v} out of 5`">{{ dots(s.v) }}</span>
                </span>
              </span>
            </span>
          </label>
        </fieldset>

        <button
          :disabled="submitting"
          @click="submit"
          class="btn-deco btn-deco--gold btn-deco--lg"
        >{{ submitting ? 'Creating…' : 'Create hero' }}</button>
      </section>

      <!-- PROFILE — Hero + Stash + Dossier, one page, three columns -->
      <section v-else-if="!loading && hero && view === 'profile'" class="profile-grid">
        <HeroCard
          :hero="hero" :stats="stats" :inventory="inventory"
          @inspect="openInspect" @filter-request="onFilterRequest" @updated="refresh"
        />

        <div class="min-w-0 min-h-0 flex flex-col">
          <Inventory
            v-if="inventory" :inventory="inventory" :filter="stashFilter"
            @update:filter="stashFilter = $event" @inspect="openInspect" @updated="refresh"
          />
        </div>

        <HeroDossier :hero="hero" :stats="stats" :inventory="inventory" @inspect="openInspect" />
      </section>

      <!-- DISTRICTS -->
      <Districts v-else-if="!loading && hero && view === 'districts'" @updated="refresh" @goto-map="onGotoMap" />

      <!-- THE QUARTER (merged Market + Shop) -->
      <Market
        v-else-if="!loading && hero && view === 'market'"
        :initial-tab="marketInitialTab"
        :spotlight-item="guide === 'nerve' ? 'nerve_refill' : null"
        @updated="refresh"
        @topup="view = 'reserve'"
        @counting="view = 'counting'"
      />

      <!-- THE COUNTING HOUSE — bars → cash, full page in the Gold Reserve's design -->
      <CountingHouse
        v-else-if="!loading && hero && view === 'counting'"
        :spotlight="guide === 'cash'"
        @updated="refresh"
        @topup="view = 'reserve'"
      />

      <!-- THE GOLD RESERVE — full page, structurally dominion's premium ShopPage -->
      <GoldReserve
        v-else-if="!loading && hero && view === 'reserve'"
        :balance="hero?.progression.goldBars"
        :spotlight="guide === 'gold'"
        @updated="refresh"
      />

      <!-- JOBS -->
      <Jobs v-else-if="!loading && hero && view === 'jobs'" @updated="refresh" />

      <!-- CHAT — "Word on the Street" (also completes the same-name job) -->
      <Chat v-else-if="!loading && hero && view === 'chat'" />

      <!-- RANKINGS -->
      <Rankings v-else-if="!loading && hero && view === 'rankings'" />

      <!-- DUNGEONS / HEISTS -->
      <Dungeons v-else-if="!loading && hero && view === 'dungeons'" :hero-class="hero?.hero.class" @updated="refresh" />

      <!-- THE PIT (arena) — interactive tactical bout vs a bot -->
      <Arena v-else-if="!loading && hero && view === 'pit'" :hero-class="hero?.hero.class" @updated="refresh" />

      <!-- SETTINGS — account, papers (KYC), purchases (dominion's Settings page) -->
      <Settings
        v-else-if="!loading && view === 'settings'"
        :user="props.user" :hero="hero"
        @goto-reserve="view = 'reserve'"
      />
      </div>
    </main>

    <ToastHost />
    <ConfirmHost />

    <!-- First-session onboarding overlay (self-hides once completed/skipped).
         It drives `view` behind itself so each beat previews its own screen
         (Pit step shows the Pit, map steps show the City Map, ...) and hands
         the app back to the default Hero view once it's done. -->
    <Tutorial
      v-if="hero && showTutorial"
      @navigate="onTutorialNav"
      @updated="refresh"
      @done="showTutorial = false; view = 'profile'"
    />


    <!-- Shared item detail modal — one instance for the stash grid, hero-card
         equipped strip and dossier paperdoll alike. -->
    <ItemModal
      v-if="inspect"
      :artifact="inspect.artifact"
      :slot="inspect.slot"
      :is-equipped="inventory?.equipped[inspect.slot]?.id === inspect.artifact.id"
      :equipped-in-slot="inventory?.equipped[inspect.slot] ?? null"
      @updated="onGearUpdated"
      @close="inspect = null"
    />
  </div>
</template>

<style scoped>
/* ── Make your bones — the Commission's wanted board ───────────────────────
   The four trades are the four landing posters, so this screen speaks the
   landing's language: aged paper, pinned to a dark wall, pick your man off it.
   NOTE: these styles are SFC-scoped and CANNOT borrow landing.css's tokens —
   those resolve only under #landing-root. Paper carries literal values. */
.make-head { margin-bottom: 1.5rem; }
.make-kicker {
  font-family: var(--font-label, monospace);
  font-weight: 700; font-size: 10px;
  letter-spacing: 0.24em; text-transform: uppercase;
  color: color-mix(in srgb, var(--color-gold) 62%, transparent);
  margin-bottom: 6px;
}
.make-title {
  font-family: var(--font-heading, serif);
  font-size: clamp(30px, 3.4vw, 42px);
  line-height: 1.02;
  color: var(--color-gold);
  letter-spacing: 0.02em;
}
.make-rule {
  display: block; width: 64px; height: 2px; margin: 10px 0 12px;
  background: linear-gradient(90deg, var(--color-gold), transparent);
}
.make-sub { font-family: var(--font-body, serif); color: var(--color-text-dim); }

/* the docket — a case-file line. Capped: a name is a few words, not a page. */
.docket { max-width: 21rem; margin-bottom: 2rem; }
.docket-label {
  display: block; margin-bottom: 5px;
  font-family: var(--font-label, monospace);
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.2em; text-transform: uppercase;
  color: var(--color-text-dim);
}
.docket-input {
  width: 100%;
  padding: 0.62rem 0.8rem;
  background: rgba(8, 6, 12, 0.75);
  border: 1px solid color-mix(in srgb, var(--color-border) 80%, transparent);
  border-radius: 4px;
  color: var(--color-text);
  font-family: var(--font-body, serif);
  font-size: 15px;
  transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
}
.docket-input::placeholder { color: var(--color-text-dim); opacity: 0.7; }
.docket-input:focus {
  outline: none;
  background: rgba(12, 9, 18, 0.9);
  border-color: color-mix(in srgb, var(--color-gold) 65%, transparent);
  box-shadow: 0 1px 0 var(--color-gold), 0 0 18px rgba(200, 162, 76, 0.12);
}

/* the wall */
.board {
  border: 0; padding: 0; margin: 0 0 2rem;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.25rem 1rem;
}
/* NOTE: the <1024 strip layout lives at the BOTTOM of this block, not here.
   `.paper > * { display:block }` and `.paper { display:block }` are declared
   below at the SAME specificity, so a media query placed here loses on source
   order alone and silently does nothing. */

.poster { position: relative; display: block; padding-top: 14px; cursor: pointer; }
.poster-radio:focus-visible + .pin + .paper { outline: 2px solid var(--color-gold-bright); outline-offset: 4px; }

/* the pin holding it to the wall */
.pin {
  position: absolute; top: 2px; left: 50%; z-index: 6;
  width: 13px; height: 13px; border-radius: 50%;
  transform: translateX(-50%);
  background: radial-gradient(circle at 34% 32%, #f2dfa4, #b8862f 62%, #6d4d16);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.7);
}

.paper {
  position: relative; display: block; overflow: hidden;
  padding: 16px 14px 14px;
  text-align: center;
  color: #241a0e;
  transform: rotate(var(--rot, 0deg));
  transform-origin: 50% 4px;
  background:
    radial-gradient(120% 55% at 15% 0%, rgba(120, 90, 40, 0.12), transparent 42%),
    radial-gradient(90% 55% at 92% 100%, rgba(80, 52, 16, 0.16), transparent 46%),
    linear-gradient(180deg, #ece0bd, #e3d3a6 58%, #d6c495);
  box-shadow: 0 18px 38px rgba(0, 0, 0, 0.6), inset 0 0 0 1px rgba(70, 50, 20, 0.3);
  transition: transform 400ms ease, box-shadow 400ms ease;
}
/* fibre + foxing: the two passes that stop the paper reading as a flat swatch */
.paper::before {
  content: ''; position: absolute; inset: 0; z-index: 1; pointer-events: none;
  mix-blend-mode: multiply; opacity: 0.3;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}
.paper::after {
  content: ''; position: absolute; inset: 0; z-index: 1; pointer-events: none;
  box-shadow: inset 0 0 34px rgba(60, 40, 12, 0.55), inset 0 0 6px rgba(40, 26, 8, 0.6);
}
.paper > * { position: relative; z-index: 2; display: block; }

.poster:hover .paper { transform: rotate(0deg) scale(1.03); box-shadow: 0 30px 60px rgba(0, 0, 0, 0.72), inset 0 0 0 1px rgba(70, 50, 20, 0.3); }
.poster--on .paper {
  transform: rotate(0deg) scale(1.04);
  box-shadow: 0 30px 62px rgba(0, 0, 0, 0.75), inset 0 0 0 1px rgba(70, 50, 20, 0.3),
              0 0 0 2px var(--color-gold), 0 0 30px rgba(200, 162, 76, 0.28);
}

/* NOTE: no per-poster "By order of The Commission" — the board header says it
   once and governs the whole wall; four copies side by side was just noise. */
.wanted {
  font-family: var(--font-heading, serif);
  font-weight: 900; font-size: clamp(26px, 2.6vw, 34px);
  line-height: 0.84; letter-spacing: 0.03em;
  text-transform: uppercase; color: #1b1207;
}
.doa {
  font-family: var(--font-label, monospace);
  font-weight: 700; font-size: 8px;
  letter-spacing: 0.18em; text-transform: uppercase;
  color: #5c3f18;
  border-block: 1.5px solid #8a6a33;
  padding: 3px 0; margin: 7px 0 10px;
}

.mug {
  position: relative;
  margin: 0 auto 10px;
  border: 5px solid #221809; background: #221809;
  box-shadow: 0 7px 16px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  aspect-ratio: 1 / 1.12;
}
.mug img {
  width: 100%; height: 100%;
  object-fit: cover; object-position: 50% 0%;
  /* the class art is a clean modern render — the sepia+contrast pass is what
     makes it read as a 1929 police photograph instead of a game asset */
  filter: sepia(0.5) contrast(1.06) brightness(0.9) saturate(0.75);
  transition: filter 450ms ease, transform 550ms ease;
}
.poster:hover .mug img,
.poster--on .mug img { filter: sepia(0.14) contrast(1.08) brightness(1) saturate(1.02); transform: scale(1.05); }
.mugcap {
  position: absolute; left: 0; right: 0; bottom: 0;
  background: rgba(18, 12, 5, 0.86);
  color: #d8c79a;
  font-family: var(--font-label, monospace);
  font-weight: 700; font-size: 9px;
  letter-spacing: 0.12em; text-transform: uppercase;
  padding: 3px;
}

.pname {
  font-family: var(--font-heading, serif);
  font-weight: 700; font-size: 20px;
  text-transform: uppercase; letter-spacing: 0.02em;
  color: #1b1207; line-height: 1;
}
.crimes {
  font-family: var(--font-body, serif);
  font-style: italic; font-size: 13px; line-height: 1.35;
  color: #40301c;
  margin: 5px 0 10px;
}
.rap {
  border-top: 1px dashed #856430; border-bottom: 1px dashed #856430;
  padding: 7px 2px;
}
.rsrow {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  font-family: var(--font-label, monospace);
  font-weight: 700; font-size: 9px;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: #5c3f18;
}
.rsrow + .rsrow { margin-top: 3px; }
.rsrow .dots { letter-spacing: 2px; font-size: 8px; color: #6a1712; }

/* stamps. Both sit INSIDE the paper — it clips (overflow:hidden), so anything
   hung off the edge loses its last letter ("AT LARG"). */
.pstamp {
  /* right:10px, not 0/negative — the -13deg swing throws the corner ~2px wider
     than the box, and the paper clips it into "AT LARG". */
  position: absolute; top: 38%; right: 10px; z-index: 5;
  transform: rotate(-13deg);
  font-family: var(--font-label, monospace);
  font-weight: 700; font-size: 8px;
  letter-spacing: 0.1em; text-transform: uppercase;
  color: rgba(140, 26, 20, 0.72);
  border: 2px solid rgba(140, 26, 20, 0.6);
  box-shadow: inset 0 0 0 1px rgba(140, 26, 20, 0.35);
  padding: 3px 6px; opacity: 0.82; pointer-events: none;
  transition: opacity 180ms ease;
}
/* the wax "MADE" slam — only on the man you picked. Lands across the mug, not
   the header, and evicts the At Large stamp so the two never overlap. */
.poster--on .pstamp { opacity: 0; }
.made {
  position: absolute; top: 45%; left: 50%; z-index: 6;
  font-family: var(--font-heading, serif);
  font-weight: 900; font-size: clamp(26px, 2.4vw, 32px);
  letter-spacing: 0.06em; text-transform: uppercase;
  color: rgba(150, 28, 22, 0.92);
  border: 3px solid rgba(150, 28, 22, 0.82);
  padding: 2px 12px;
  opacity: 0; pointer-events: none;
  text-shadow: 0 1px 0 rgba(255, 240, 200, 0.25);
  transform: translate(-50%, -50%) rotate(-9deg) scale(2.4);
  transition: opacity 180ms ease, transform 220ms cubic-bezier(0.2, 1.5, 0.4, 1);
}
.poster--on .made { opacity: 0.94; transform: translate(-50%, -50%) rotate(-9deg) scale(1); }

/* Below 4-across, a full poster per class cannot fit the viewport — two rows of
   them push CREATE HERO under the fold and force a zoom-out. So the poster lies
   down into a pinned case-file strip: mug left, details right. Same paper, same
   stamps, ~a third of the height. MUST stay last: it overrides `.paper` and
   `.paper > *` at equal specificity, so it only wins on source order. */
@media (max-width: 1023.98px), (max-height: 900px) {
  .board { grid-template-columns: minmax(0, 1fr); gap: 0.7rem; }
  .poster { padding-top: 0; }
  .pin { display: none; }
  .paper {
    display: grid;
    grid-template-columns: 74px minmax(0, 1fr);
    grid-template-areas: 'mug name' 'mug crimes' 'mug rap';
    align-content: center;
    gap: 1px 12px;
    padding: 10px 12px;
    text-align: left;
    transform: rotate(0deg);
  }
  .wanted, .doa { display: none; }
  .mug { grid-area: mug; margin: 0; align-self: center; aspect-ratio: 1 / 1.16; border-width: 3px; }
  .mugcap { font-size: 6px; letter-spacing: 0.04em; padding: 1px 0; }
  .pname { grid-area: name; font-size: 17px; }
  .crimes { grid-area: crimes; margin: 2px 0 5px; font-size: 12px; }
  .rap { grid-area: rap; border-bottom: 0; padding: 5px 0 0; }
  .rsrow { font-size: 8px; }
  .pstamp { top: 8px; right: 8px; font-size: 7px; padding: 2px 5px; }
  /* takes the At Large stamp's corner (which hides on select) — centred it just
     sat on top of the rap-sheet dots and hid the very stats you're comparing */
  .made {
    font-size: 17px; top: 11px; left: auto; right: 10px;
    padding: 1px 8px; border-width: 2px;
    transform: rotate(-9deg) scale(2.4);
  }
  .poster--on .made { transform: rotate(-9deg) scale(1); }
  .poster:hover .paper, .poster--on .paper { transform: rotate(0deg) scale(1.01); }
}

/* A short-but-wide window (a 1462x768 laptop is the common case) gets strips for
   height, but has width to spare — so stand them two-up instead of one long
   column, which halves the board again and clears the fold. */
@media (min-width: 760px) and (max-height: 900px) {
  .board { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.7rem 0.9rem; }
}

@media (prefers-reduced-motion: reduce) {
  .paper, .mug img, .made { transition: none; }
  .poster:hover .paper, .poster--on .paper { transform: rotate(0deg); }
  .poster--on .made { transform: translate(-50%, -50%) rotate(-9deg); }
}

/* ── Hero page — 3-column layout (hero card | stash | dossier) ─────────── */
.profile-main { overflow-y: auto; }

.profile-grid {
  display: grid;
  grid-template-columns: 260px 1fr 280px;
  gap: 1.25rem;
  align-items: start;
}
@media (min-width: 1400px) {
  .profile-grid { grid-template-columns: 280px 1fr 340px; }
}
/* viewport-locked profile: columns stretch to the row, never past it */
@media (min-width: 1101px) {
  .profile-main { overflow: hidden; }
  .profile-grid {
    height: 100%;
    grid-template-rows: minmax(0, 1fr);
    align-items: stretch;
  }
  .profile-grid > * { min-height: 0; }
}
/* Below the viewport lock the page itself scrolls, and the wrapper's h-full
   swallows its own py-4 once content overflows — the last card would sit flush
   against the screen edge at max scroll. Real breathing room lives here. */
@media (max-width: 1100px) {
  .profile-grid { grid-template-columns: 1fr; padding-bottom: 2.5rem; }
}

/* ── Mobile burger + drawer ───────────────────────────────────────────── */
/* hidden on desktop (scoped rule beats Tailwind's lg:hidden); shown <1024px */
.hdr-burger {
  display: none; place-items: center;
  width: 38px; height: 38px; flex: none;
  color: var(--color-gold);
  border: 1px solid color-mix(in srgb, var(--color-gold) 40%, transparent);
  background: rgba(0, 0, 0, 0.28); border-radius: 8px;
  transition: color 150ms ease, border-color 150ms ease, background 150ms ease;
}
@media (max-width: 1023.98px) { .hdr-burger { display: grid; } }
@media (min-width: 1024px) { .hdr-drawer-scrim { display: none; } }
.hdr-burger:hover { color: var(--color-gold-bright); border-color: color-mix(in srgb, var(--color-gold) 65%, transparent); }
.hdr-burger:active { transform: translateY(1px); }

.hdr-drawer-scrim {
  position: fixed; inset: 0; z-index: 300;
  background: rgba(4, 3, 8, 0.62); backdrop-filter: blur(3px);
  display: flex; justify-content: flex-end;
}
.hdr-drawer {
  width: min(82vw, 320px); height: 100%;
  display: flex; flex-direction: column;
  padding: 14px 14px 18px;
  background: linear-gradient(180deg, #14101c, #0b0812);
  border-left: 1px solid color-mix(in srgb, var(--color-gold) 30%, transparent);
  box-shadow: -18px 0 50px rgba(0, 0, 0, 0.6);
  overflow-y: auto;
}
.hdr-drawer-head {
  display: flex; align-items: center; justify-content: space-between;
  padding-bottom: 12px; margin-bottom: 8px;
  border-bottom: 1px solid color-mix(in srgb, var(--color-border) 70%, transparent);
}
.hdr-drawer-x {
  display: grid; place-items: center; width: 34px; height: 34px;
  color: var(--color-text-dim); border-radius: 8px;
}
.hdr-drawer-x:hover { color: var(--color-text); background: rgba(255, 255, 255, 0.05); }
.hdr-drawer-nav { display: flex; flex-direction: column; gap: 2px; flex: 1; }
/* Drawer entries as speakeasy ledger lines: smallcap serif with a gold ◆ marker
   and a hairline underline on the active row — the generic left-border-accent
   read as unstyled boilerplate next to the rest of the chrome. */
.hdr-drawer-item {
  position: relative;
  text-align: left;
  padding: 13px 12px 13px 26px;
  font-family: var(--font-heading, serif);
  font-size: 16px; letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--color-text-dim);
  transition: color 150ms ease, background 150ms ease;
}
.hdr-drawer-item::before {
  content: '◆';
  position: absolute; left: 8px; top: 50%; transform: translateY(-50%) scale(0.6);
  font-size: 10px; color: var(--color-gold);
  opacity: 0; transition: opacity 150ms ease, transform 200ms ease;
}
.hdr-drawer-item::after {
  content: '';
  position: absolute; left: 26px; right: 40%; bottom: 8px; height: 1px;
  background: linear-gradient(90deg, var(--color-gold), transparent);
  opacity: 0; transition: opacity 150ms ease;
}
.hdr-drawer-item:hover { color: var(--color-text); background: rgba(200, 162, 76, 0.05); }
.hdr-drawer-item.is-active { color: var(--color-gold-bright); }
.hdr-drawer-item.is-active::before { opacity: 1; transform: translateY(-50%) scale(1); }
.hdr-drawer-item.is-active::after { opacity: 1; }
.hdr-drawer-out { width: 100%; margin-top: 14px; }

/* slide-in transition */
.drawer-enter-active, .drawer-leave-active { transition: opacity 200ms ease; }
.drawer-enter-active .hdr-drawer, .drawer-leave-active .hdr-drawer { transition: transform 220ms cubic-bezier(0.2, 0.8, 0.25, 1); }
.drawer-enter-from, .drawer-leave-to { opacity: 0; }
.drawer-enter-from .hdr-drawer, .drawer-leave-to .hdr-drawer { transform: translateX(100%); }
@media (prefers-reduced-motion: reduce) {
  .drawer-enter-active .hdr-drawer, .drawer-leave-active .hdr-drawer { transition: none; }
}
</style>
